/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ChevronLeft, CreditCard, Truck, MapPin, Package, 
  ShieldCheck, CheckCircle2, Wallet, 
  Lock, ArrowRight, Loader2, AlertCircle
} from 'lucide-react';
import { cartService } from '../api/cart';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

type PaymentMethod = 'razorpay' | 'cod';
type CheckoutStep = 'shipping' | 'payment' | 'confirmation';

interface LocationState {
  isBuyNow?: boolean;
  buyNowProduct?: CartItem;
  cartItems?: CartItem[];
}

interface PaymentSettings {
  razorpayEnabled: boolean;
  razorpayKeyId: string;
  codEnabled: boolean;
  globalCodEnabled?: boolean;
  currency: string;
}

interface CodEligibility {
  globalCodEnabled: boolean;
  cartCodEligible: boolean;
  codAvailable: boolean;
  reason: string | null;
  blockedProducts?: { productId: string; name: string }[];
}

interface CartItem {
  _id: string;
  productId?: string;
  name: string;
  price: {
    selling: number;
    mrp: number;
  } | number;
  images: { url: string }[] | string[];
  quantity: number;
  image?: string;
  variant?: {
    color?: string;
    size?: string;
    price?: number;
  };
}

const isValidObjectId = (value: unknown) => {
  if (!value) return false;
  const s = String(value);
  return /^[a-f\d]{24}$/i.test(s);
};

const getOrderProductId = (item: CartItem): string => {
  const anyItem: any = item as any;
  const candidates = [
    item.productId,
    anyItem.product?._id,
    anyItem.productId,
    item._id
  ];
  const found = candidates.find((c) => isValidObjectId(c));
  return found ? String(found) : String(item.productId || anyItem.productId || item._id);
};

const CheckoutPage: React.FC = () => {
  const location = useLocation();
  const isBuyNow = (location.state as LocationState)?.isBuyNow;
  const buyNowProduct = (location.state as LocationState)?.buyNowProduct;
  const locationCartItems = (location.state as LocationState)?.cartItems;
  
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('shipping');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('razorpay');
  const [loading, setLoading] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
  const [codEligibility, setCodEligibility] = useState<CodEligibility | null>(null);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [cartLoading, setCartLoading] = useState(true);
  const [cartError, setCartError] = useState<string | null>(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{code: string; discount: number; discountType: string} | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India'
  });

  const subtotal = cartItems.reduce((total, item) => total + ((typeof item.price === 'number' ? item.price : item.price?.selling || 0) * item.quantity), 0);
  const [shippingQuote, setShippingQuote] = useState<{
    available: boolean;
    shippingCharge: number;
    codCharge: number;
    estimatedDelivery?: string;
    couriers?: { courierName: string; rate: number; estimatedDeliveryDays?: string }[];
    fastest?: { courierName: string; etd?: string; estimatedDeliveryDays?: string };
    freeShipping?: boolean;
    message?: string;
    package?: Record<string, unknown>;
    packageSummary?: Record<string, unknown>;
  } | null>(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState<{ courierCompanyId?: number; courierName: string; rate: number } | null>(null);

  const shipping = shippingQuote?.freeShipping ? 0 : (selectedCourier?.rate ?? shippingQuote?.shippingCharge ?? 0);
  const codExtra = paymentMethod === 'cod' ? (shippingQuote?.codCharge ?? 0) : 0;
  const tax = 0;
  
  // Calculate discount
  let discount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.discountType === 'percentage') {
      discount = Math.round(subtotal * (appliedCoupon.discount / 100));
    } else {
      discount = appliedCoupon.discount;
    }
  }
  
  const total = subtotal + shipping + codExtra + tax - discount;

  const globalCodOn = paymentSettings?.globalCodEnabled !== false && paymentSettings?.codEnabled !== false;
  const cartCodOk = codEligibility?.codAvailable === true;
  const showCodOption = globalCodOn && cartCodOk;
  const codUnavailableMessage =
    !globalCodOn
      ? 'Cash on Delivery is currently unavailable on this store.'
      : codEligibility?.reason || 'Cash on Delivery is not available for items in your cart.';

  const fetchCodEligibility = async (items: CartItem[]) => {
    if (items.length === 0) {
      setCodEligibility(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/payment/cod-eligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({
            productId: getOrderProductId(item),
            quantity: item.quantity,
          })),
        }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        setCodEligibility(data.data);
      }
    } catch (e) {
      console.error('COD eligibility check failed:', e);
    }
  };

  const fetchShippingQuote = async (pincode: string, cod: boolean) => {
    if (!pincode || pincode.length !== 6 || cartItems.length === 0) return;
    setShippingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/shipping/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          deliveryPincode: pincode,
          cod,
          items: cartItems.map((item) => ({
            productId: getOrderProductId(item),
            quantity: item.quantity,
          })),
        }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        setShippingQuote(data.data);
        if (data.data.globalCodEnabled !== undefined || data.data.cartCodEligible !== undefined) {
          setCodEligibility({
            globalCodEnabled: data.data.globalCodEnabled !== false,
            cartCodEligible: data.data.cartCodEligible !== false,
            codAvailable: data.data.codAvailable !== false,
            reason: data.data.codUnavailableReason || null,
            blockedProducts: data.data.blockedProducts,
          });
        }
        const cheapest = data.data.cheapest || data.data.couriers?.[0];
        if (cheapest) {
          setSelectedCourier({
            courierCompanyId: cheapest.courierCompanyId,
            courierName: cheapest.courierName,
            rate: cheapest.rate,
          });
        }
      } else {
        setShippingQuote({ available: false, shippingCharge: 0, codCharge: 0, message: data.message });
      }
    } catch (e) {
      setShippingQuote({ available: false, shippingCharge: 0, codCharge: 0, message: 'Could not load shipping rates' });
    } finally {
      setShippingLoading(false);
    }
  };

  useEffect(() => {
    if (shippingInfo.zipCode?.length === 6 && cartItems.length > 0) {
      fetchShippingQuote(shippingInfo.zipCode, paymentMethod === 'cod' && showCodOption);
    }
  }, [shippingInfo.zipCode, paymentMethod, cartItems.length, showCodOption]);

  // Load Razorpay Script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const loadCart = async () => {
    try {
      setCartLoading(true);
      setCartError(null);

      // If buyNow, use the product directly
      if (isBuyNow && buyNowProduct) {
        setCartItems([{
          _id: buyNowProduct._id,
          name: buyNowProduct.name,
          price: buyNowProduct.price,
          images: buyNowProduct.images || [],
          quantity: buyNowProduct.quantity || 1
        }]);
        setCartLoading(false);
        return;
      }

      // If cart items passed from location state, use them
      if (locationCartItems && locationCartItems.length > 0) {
        setCartItems(locationCartItems);
        setCartLoading(false);
        return;
      }

      // Normal cart flow from API
      const response = await cartService.getCart();

      if (response.success && response.data) {
        setCartItems(response.data.items || []);
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      setCartError('Failed to load cart. Please try again.');
      setCartItems([]);
    } finally {
      setCartLoading(false);
    }
  };

  // Load cart from API
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadCart();
  }, [isBuyNow, buyNowProduct]);

  useEffect(() => {
    fetchPaymentSettings();
  }, []);

  useEffect(() => {
    if (cartItems.length > 0) {
      fetchCodEligibility(cartItems);
    }
  }, [cartItems]);

  useEffect(() => {
    if (!showCodOption && paymentMethod === 'cod') {
      if (paymentSettings?.razorpayEnabled !== false) {
        setPaymentMethod('razorpay');
      }
    }
  }, [showCodOption, paymentMethod, paymentSettings?.razorpayEnabled]);

  useEffect(() => {
    if (currentStep !== 'payment') return;
    const interval = window.setInterval(() => {
      fetchPaymentSettings();
      if (cartItems.length > 0) fetchCodEligibility(cartItems);
    }, 20000);
    return () => window.clearInterval(interval);
  }, [currentStep, cartItems]);

  const fetchPaymentSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/payment/public`);
      const data = await response.json();
      if (data.success) {
        // Show Razorpay if enabled OR if keyId is present
        const razorpayEnabled = data.data.razorpay.enabled || (data.data.razorpay.keyId && data.data.razorpay.keyId.length > 0);
        const settings = {
          razorpayEnabled: razorpayEnabled,
          razorpayKeyId: data.data.razorpay.keyId,
          codEnabled: data.data.cod.enabled,
          globalCodEnabled: data.data.cod.globalEnabled ?? data.data.cod.enabled,
          currency: data.data.currency,
        };
        setPaymentSettings(settings);
        if (settings.razorpayEnabled) {
          setPaymentMethod('razorpay');
        } else if (settings.codEnabled) {
          setPaymentMethod('cod');
        }
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
      setPaymentSettings({
        razorpayEnabled: true,
        razorpayKeyId: '',
        codEnabled: true,
        currency: 'INR'
      });
    }
  };

  // Apply coupon code
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    setCouponError(null);
    
    try {
      const response = await fetch(`${API_URL}/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          code: couponCode.trim().toUpperCase(),
          totalAmount: subtotal
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.data && data.data.isValid) {
        const discountAmount = data.data.discountAmount || data.data.coupon?.discountAmount || 0;
        const discountPercentage = data.data.coupon?.discountPercentage || 0;
        setAppliedCoupon({
          code: data.data.coupon?.code || couponCode,
          discount: discountPercentage > 0 ? discountPercentage : discountAmount,
          discountType: discountPercentage > 0 ? 'percentage' : 'fixed'
        });
      } else {
        setCouponError(data.message || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Coupon error:', error);
      setCouponError('Failed to apply coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShippingInfo({ ...shippingInfo, [e.target.name]: e.target.value });
  };

  const handleContinueToPayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shippingInfo.firstName || !shippingInfo.lastName || !shippingInfo.email || 
        !shippingInfo.phone || !shippingInfo.address || !shippingInfo.city || 
        !shippingInfo.state || !shippingInfo.zipCode) {
      alert('Please fill all required fields');
      return;
    }
    setCurrentStep('payment');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const createOrder = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: cartItems.map((item) => ({
            product: getOrderProductId(item),
            name: item.name,
            image: (typeof item.images?.[0] === 'string' ? item.images[0] : item.images?.[0]?.url) || (item as any).image || '',
            quantity: item.quantity,
            price: typeof item.price === 'number' ? item.price : (item.price?.selling || 0),
            variant: (item as any).variant
          })),
          shippingAddress: {
            firstName: shippingInfo.firstName,
            lastName: shippingInfo.lastName,
            email: shippingInfo.email,
            phone: shippingInfo.phone,
            address: shippingInfo.address,
            city: shippingInfo.city,
            state: shippingInfo.state,
            zipCode: shippingInfo.zipCode,
            country: shippingInfo.country
          },
          paymentMethod,
          subtotal,
          shipping,
          tax,
          total: subtotal + shipping + codExtra + tax - discount,
          shippingDetails: {
            shippingCharge: shipping,
            codCharge: codExtra,
            courierCharge: shipping,
            courierCompanyId: selectedCourier?.courierCompanyId,
            courierName: selectedCourier?.courierName,
            estimatedDelivery: shippingQuote?.estimatedDelivery || selectedCourier?.courierName,
            package: shippingQuote?.packageSummary || shippingQuote?.package,
          },
          coupon: appliedCoupon ? appliedCoupon.code : null
        })
      });

      const data = await response.json();
      if (data.success) {
        return data.order;
      }
      throw new Error(data.message || 'Failed to create order');
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  };

  const reportPaymentFailed = async (orderId: string, reason: string, code?: string) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/payment/mark-failed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ orderId, reason, code }),
    });
  };

  const reportPaymentCancelled = async (orderId: string, reason?: string) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/payment/mark-cancelled`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ orderId, reason: reason || 'Payment window closed' }),
    });
  };

  const handleRazorpayPayment = async () => {
    setLoading(true);
    let checkoutOrderId: string | null = null;
    try {
      const order = await createOrder();
      checkoutOrderId = order._id;
      setOrderId(order._id);

      const token = localStorage.getItem('token');
      const razorpayResponse = await fetch(`${API_URL}/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          orderId: order._id,
          amount: total,
          currency: 'INR'
        })
      });

      const razorpayData = await razorpayResponse.json();
      if (!razorpayData.success) {
        throw new Error(razorpayData.message || 'Failed to create Razorpay order');
      }

      const options = {
        key: razorpayData.data.keyId,
        amount: razorpayData.data.amount,
        currency: razorpayData.data.currency,
        name: 'NLS Store',
        description: `Order #${order.orderNumber}`,
        order_id: razorpayData.data.razorpayOrderId,
        handler: async function (response: any) {
          try {
            const verifyResponse = await fetch(`${API_URL}/payment/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                orderId: order._id,
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature
              })
            });

            const verifyData = await verifyResponse.json();
            if (verifyData.success) {
              setOrderPlaced(true);
              setCurrentStep('confirmation');
              localStorage.removeItem('cartItems');
              setCartItems([]);
              setLoading(false);
            } else {
              await reportPaymentFailed(order._id, verifyData.message || 'Verification failed', 'VERIFY_FAILED');
              alert(
                'Payment could not be verified. Your order was not placed. No charge was confirmed. You may try checkout again or contact support if money was debited.'
              );
              setLoading(false);
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            await reportPaymentFailed(order._id, 'Verification error', 'VERIFY_ERROR');
            alert(
              'Payment verification failed. Your order was not placed. If amount was debited, contact support with your payment reference.'
            );
            setLoading(false);
          }
        },
        modal: {
          ondismiss: async function () {
            if (checkoutOrderId) {
              await reportPaymentCancelled(checkoutOrderId, 'Payment cancelled');
            }
            setLoading(false);
          },
        },
        prefill: {
          name: `${shippingInfo.firstName} ${shippingInfo.lastName}`,
          email: shippingInfo.email,
          contact: shippingInfo.phone
        },
        notes: {
          order_id: order._id,
          order_number: order.orderNumber
        },
        theme: {
          color: '#3B82F6'
        }
      };

      const rzp = new (window as any).Razorpay(options);
      
      rzp.on('payment.failed', async function (response: any) {
        console.error('Payment failed:', response);
        if (checkoutOrderId) {
          await reportPaymentFailed(
            checkoutOrderId,
            response?.error?.description || 'Payment failed',
            response?.error?.code
          );
        }
        alert(
          `Payment failed: ${response?.error?.description || 'Unknown error'}. Your order was not placed — you can try again.`
        );
        setLoading(false);
      });

      rzp.open();
      setLoading(false);
    } catch (error) {
      console.error('Payment error:', error);
      if (checkoutOrderId) {
        await reportPaymentFailed(
          checkoutOrderId,
          error instanceof Error ? error.message : 'Checkout error',
          'CHECKOUT_ERROR'
        );
      }
      alert(`Payment could not be started: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setLoading(false);
    }
  };

  const handleCODOrder = async () => {
    setLoading(true);
    try {
      const order = await createOrder();
      setOrderId(order._id);
      setOrderPlaced(true);
      setCurrentStep('confirmation');
      localStorage.removeItem('cartItems');
      setCartItems([]);
    } catch (error) {
      console.error('Order error:', error);
      alert(`Failed to place order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (paymentMethod === 'cod' && !showCodOption) {
      alert(codUnavailableMessage);
      return;
    }
    if (paymentMethod === 'razorpay') {
      await handleRazorpayPayment();
    } else {
      await handleCODOrder();
    }
  };

  if (cartLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <p className="text-gray-600 font-medium">Loading cart...</p>
        </div>
      </div>
    );
  }

  if (cartError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-gray-100">
          <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{cartError}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-2xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-500/30"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0 && !orderPlaced) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-gray-100">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Package className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Looks like you haven't added anything to your cart yet.</p>
          <Link
            to="/products"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-2xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-500/30"
          >
            Continue Shopping
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  if (currentStep === 'confirmation' && orderPlaced) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center border border-green-100">
          <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Placed Successfully!</h2>
          <p className="text-gray-600 mb-4">Thank you for your purchase.</p>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 mb-6 border border-green-200">
            <p className="text-sm text-gray-600 mb-1">Order ID</p>
            <p className="text-lg font-bold text-gray-900">{orderId}</p>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            We've sent a confirmation email to {shippingInfo.email}
          </p>
          <div className="space-y-3">
            <Link
              to="/orders"
              className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-2xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-500/30"
            >
              View My Orders
            </Link>
            <Link
              to="/"
              className="block w-full bg-gray-100 text-gray-700 px-8 py-3 rounded-2xl font-semibold hover:bg-gray-200 transition-all"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/cart" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">Back to Cart</span>
            </Link>
            <h1 className="text-xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Checkout</h1>
            <div className="w-24"></div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className={`flex items-center gap-2 ${currentStep === 'shipping' ? 'text-blue-600' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center shadow-md ${currentStep === 'shipping' ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gradient-to-br from-green-500 to-green-600 text-white'}`}>
                {currentStep === 'shipping' ? <Truck className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              </div>
              <span className="hidden sm:inline font-medium text-sm">Shipping</span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-blue-300 to-gray-300 mx-4"></div>
            <div className={`flex items-center gap-2 ${currentStep === 'payment' ? 'text-blue-600' : currentStep === 'confirmation' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center shadow-md ${currentStep === 'payment' ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : currentStep === 'confirmation' ? 'bg-gradient-to-br from-green-500 to-green-600 text-white' : 'bg-gray-200'}`}>
                {currentStep === 'confirmation' ? <CheckCircle2 className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
              </div>
              <span className="hidden sm:inline font-medium text-sm">Payment</span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-gray-300 mx-4"></div>
            <div className={`flex items-center gap-2 ${currentStep === 'confirmation' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center shadow-md ${currentStep === 'confirmation' ? 'bg-gradient-to-br from-green-500 to-green-600 text-white' : 'bg-gray-200'}`}>
                <Package className="w-4 h-4" />
              </div>
              <span className="hidden sm:inline font-medium text-sm">Confirmation</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {currentStep === 'shipping' ? (
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-gray-100/50 overflow-hidden">
                <div className="p-6 border-b border-gray-200/50 bg-gradient-to-r from-blue-50 to-purple-50">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    Shipping Information
                  </h2>
                  <p className="text-gray-600 mt-1">Enter your delivery details</p>
                </div>
                <form onSubmit={handleContinueToPayment} className="p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        value={shippingInfo.firstName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all bg-white/50 hover:bg-white"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        value={shippingInfo.lastName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all bg-white/50 hover:bg-white"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={shippingInfo.email}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all bg-white/50 hover:bg-white"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={shippingInfo.phone}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all bg-white/50 hover:bg-white"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      required
                      value={shippingInfo.address}
                      onChange={handleInputChange}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all bg-white/50 hover:bg-white"
                      placeholder="123, Main Street, Apartment 4B"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        required
                        value={shippingInfo.city}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all bg-white/50 hover:bg-white"
                        placeholder="Mumbai"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        required
                        value={shippingInfo.state}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all bg-white/50 hover:bg-white"
                        placeholder="Maharashtra"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PIN Code *
                      </label>
                      <input
                        type="text"
                        name="zipCode"
                        required
                        value={shippingInfo.zipCode}
                        onChange={handleInputChange}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all bg-white/50 hover:bg-white"
                        placeholder="400001"
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-4 rounded-2xl transition-all shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
                    >
                      Continue to Payment
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-gray-100/50 overflow-hidden">
                <div className="p-6 border-b border-gray-200/50 bg-gradient-to-r from-blue-50 to-purple-50">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    Payment Method
                  </h2>
                  <p className="text-gray-600 mt-1">Choose how you want to pay</p>
                </div>
                <div className="p-6 space-y-4">
                  {/* Razorpay Option */}
                  {paymentSettings?.razorpayEnabled !== false && (
                    <label className={`flex items-start gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${paymentMethod === 'razorpay' ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg' : 'border-gray-200 hover:border-gray-300 bg-white/50'}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="razorpay"
                        checked={paymentMethod === 'razorpay'}
                        onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                        className="mt-1 w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                            <CreditCard className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Online Payment</p>
                            <p className="text-sm text-gray-600">Credit/Debit Card, UPI, Net Banking</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                          <Lock className="w-4 h-4" />
                          <span>Secure payment powered by Razorpay</span>
                        </div>
                      </div>
                    </label>
                  )}

                  {globalCodOn && !cartCodOk && cartItems.length > 0 && (
                    <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" />
                      <p>{codUnavailableMessage}</p>
                    </div>
                  )}

                  {!globalCodOn && (
                    <div className="flex gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                      <AlertCircle className="h-5 w-5 shrink-0 text-gray-500" />
                      <p>Cash on Delivery is currently unavailable. Please pay online.</p>
                    </div>
                  )}

                  {showCodOption && (
                    <label className={`flex items-start gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${paymentMethod === 'cod' ? 'border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg' : 'border-gray-200 hover:border-gray-300 bg-white/50'}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cod"
                        checked={paymentMethod === 'cod'}
                        onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                        className="mt-1 w-4 h-4 text-green-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-md">
                            <Wallet className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Cash on Delivery</p>
                            <p className="text-sm text-gray-600">Pay when you receive your order</p>
                          </div>
                        </div>
                      </div>
                    </label>
                  )}

                  {/* Shipping Address Summary */}
                  <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-4 mt-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-gray-900">Deliver to:</p>
                      <button
                        onClick={() => setCurrentStep('shipping')}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Change
                      </button>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p className="font-medium text-gray-800">{shippingInfo.firstName} {shippingInfo.lastName}</p>
                      <p>{shippingInfo.address}</p>
                      <p>{shippingInfo.city}, {shippingInfo.state} - {shippingInfo.zipCode}</p>
                      <p>{shippingInfo.phone}</p>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setCurrentStep('shipping')}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 rounded-2xl transition-all"
                    >
                      Back to Shipping
                    </button>
                    <button
                      onClick={handlePlaceOrder}
                      disabled={loading}
                      className="flex-[2] bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:bg-gray-400 text-white font-semibold py-4 rounded-2xl transition-all shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : paymentMethod === 'razorpay' ? (
                        <>
                          Pay ₹{total.toLocaleString('en-IN')}
                          <ArrowRight className="w-5 h-5" />
                        </>
                      ) : (
                        <>
                          Place Order (COD)
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-center text-center p-4 bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-100/50">
                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-200 rounded-xl flex items-center justify-center mb-2 shadow-md">
                  <ShieldCheck className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-xs font-medium text-gray-700">Secure Payment</p>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-100/50">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-200 rounded-xl flex items-center justify-center mb-2 shadow-md">
                  <Lock className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-xs font-medium text-gray-700">Data Privacy</p>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-gray-100/50 sticky top-24">
              <div className="p-6 border-b border-gray-200/50 bg-gradient-to-r from-blue-50 to-purple-50 rounded-t-3xl">
                <h3 className="text-lg font-bold text-gray-900">Order Summary</h3>
                <p className="text-gray-600 text-sm">{cartItems.length} items</p>
              </div>
              <div className="p-6">
                {/* Cart Items */}
                <div className="space-y-4 max-h-64 overflow-y-auto mb-6">
                  {cartItems.map((item) => (
                    <div key={item._id} className="flex gap-3">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl overflow-hidden flex-shrink-0 shadow-md">
                        <img
                          src={typeof item.images?.[0] === 'string' ? item.images[0] : item.images?.[0]?.url || item.image || '/placeholder-product.png'}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder-product.png';
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                        {item.variant && typeof item.variant === 'object' && (
                          <p className="text-xs text-gray-500">
                            {item.variant.color && <span>Color: {item.variant.color}</span>}
                            {item.variant.color && item.variant.size && <span> | </span>}
                            {item.variant.size && <span>Size: {item.variant.size}</span>}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                        <p className="text-sm font-semibold text-gray-900">
                          ₹{((typeof item.price === 'number' ? item.price : item.price?.selling || 0) * item.quantity).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Coupon Code Section */}
                <div className="border-t border-gray-200/50 pt-4 mb-4">
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-3 border border-green-200">
                      <div>
                        <span className="text-sm font-medium text-green-700">{appliedCoupon.code}</span>
                        <span className="text-xs text-green-600 ml-2">
                          ({appliedCoupon.discountType === 'percentage' ? `${appliedCoupon.discount}% OFF` : `Flat ₹${appliedCoupon.discount} OFF`})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={removeCoupon}
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Enter coupon code"
                          className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 bg-white/50 hover:bg-white transition-all"
                        />
                        <button
                          type="button"
                          onClick={applyCoupon}
                          disabled={couponLoading}
                          className="px-5 py-2.5 text-sm font-medium text-blue-600 border-2 border-blue-600 rounded-xl hover:bg-blue-50 disabled:opacity-50 transition-all"
                        >
                          {couponLoading ? '...' : 'Apply'}
                        </button>
                      </div>
                      {couponError && (
                        <p className="text-xs text-red-600 mt-2">{couponError}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 border-t border-gray-200/50 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">₹{subtotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium">
                      {shippingLoading ? '…' : shippingQuote?.freeShipping ? 'FREE' : `₹${shipping.toLocaleString('en-IN')}`}
                    </span>
                  </div>
                  {codExtra > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">COD charges</span>
                      <span className="font-medium">₹{codExtra.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {shippingQuote?.estimatedDelivery && (
                    <p className="text-xs text-gray-500">Est. delivery: {shippingQuote.estimatedDelivery}</p>
                  )}
                  {shippingQuote?.fastest?.courierName && (
                    <p className="text-xs text-blue-600">Fastest: {shippingQuote.fastest.courierName} ({shippingQuote.fastest.etd || shippingQuote.fastest.estimatedDeliveryDays})</p>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">GST (18%)</span>
                    <span className="text-green-600 font-medium">Included</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600">Discount</span>
                      <span className="text-green-600 font-medium">-₹{discount.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold border-t-2 border-gray-200 pt-4 bg-gradient-to-r from-blue-50 to-purple-50 -mx-6 px-6 py-4 rounded-b-3xl">
                    <span className="text-gray-900">Total</span>
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">₹{total.toLocaleString('en-IN')}</span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
